import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { derivePricingFromCountry } from "@/lib/pricing";

type ApplyForm = {
  deckId?: string | null;
  company: string;
  elevatorPitch: string;
  stage: string;
  raising: string;
  valuation?: string;
  vcBacked?: boolean;
  mrr?: string;
  revenueModel?: string;
  location: string;
  founderEmail: string;
};

function rzpKeys() {
  const keyId = process.env.RAZORPAY_KEY_ID;
  const keySecret = process.env.RAZORPAY_KEY_SECRET;
  if (!keyId || !keySecret) {
    throw new Error("Razorpay is not configured yet - add RAZORPAY_KEY_ID and RAZORPAY_KEY_SECRET.");
  }
  return { keyId, keySecret };
}

function basicAuth(keyId: string, keySecret: string): string {
  return "Basic " + btoa(`${keyId}:${keySecret}`);
}

async function hmacSha256Hex(secret: string, message: string): Promise<string> {
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey(
    "raw",
    enc.encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const sig = await crypto.subtle.sign("HMAC", key, enc.encode(message));
  return Array.from(new Uint8Array(sig)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export const createCheckout = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { origin: string; consent: boolean; form: ApplyForm }) => {
    if (!input?.origin || typeof input.origin !== "string") throw new Error("origin required");
    if (input.consent !== true) throw new Error("Consent required.");
    if (!input.form || typeof input.form !== "object") throw new Error("form required");
    const f = input.form;
    if (!f.company || !f.elevatorPitch || !f.founderEmail) {
      throw new Error("company, elevatorPitch and founderEmail required");
    }
    return input;
  })
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/admin.server");
    const userId = context.userId;
    const deckId = data.form.deckId || null;

    // The Razorpay Payment Link is ALWAYS created in INR.
    // Why: foreign-currency (USD) payment links are not enabled on this Razorpay
    // account, so creating a USD link fails and international founders end up stuck
    // with a pending_payment row and no link. International cards ARE enabled, so an
    // INR link is payable from anywhere. Display pricing stays geo-based via
    // usePricing() on the client; only the charge currency is pinned here.
    const country = (() => {
      try { return getRequestHeader("cf-ipcountry") ?? null; } catch { return null; }
    })();
    // Reporting only (admin insights). "XX"/"T1" are Cloudflare's unknown/Tor codes.
    const buyerCountry = (() => {
      const c = (country ?? "").trim().toUpperCase();
      return /^[A-Z]{2}$/.test(c) && c !== "XX" && c !== "T1" ? c : null;
    })();
    const inrPricing = derivePricingFromCountry("IN");
    const RZP_AMOUNT = inrPricing.submissionAmountMinor; // 199900 paise
    const RZP_CURRENCY = inrPricing.currency; // "INR"

    const nowIso = new Date().toISOString();
    const { data: inserted, error: insErr } = await supabaseAdmin
      .from("submissions")
      .insert({
        user_id: userId,
        deck_id: deckId,
        one_pager: data.form as never,
        status: "pending_payment",
        amount_cents: RZP_AMOUNT,
        currency: RZP_CURRENCY.toLowerCase(),
        authorized: true,
        authorized_at: nowIso,
        authorization_version: "v1",
        buyer_country: buyerCountry,
      })
      .select("id")
      .single();
    if (insErr || !inserted) throw insErr ?? new Error("Failed to create submission");
    const submissionId = inserted.id;

    if (deckId) {
      await supabaseAdmin
        .from("decks")
        .update({ user_id: userId })
        .eq("id", deckId)
        .is("user_id", null);
    }



    const { keyId, keySecret } = rzpKeys();
    const body = {
      amount: RZP_AMOUNT,
      currency: RZP_CURRENCY,
      accept_partial: false,
      reference_id: submissionId,
      description: "DeckScore - Investor Submission",
      customer: { email: data.form.founderEmail, name: data.form.company },
      notify: { email: false, sms: false },
      reminder_enable: false,
      callback_url: `${data.origin}/apply-success?sub=${submissionId}`,
      callback_method: "get",
    };

    const res = await fetch("https://api.razorpay.com/v1/payment_links", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: basicAuth(keyId, keySecret) },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Razorpay payment link error ${res.status}: ${text}`);
    }
    const link = await res.json();
    const linkId: string = link?.id ?? "";
    const shortUrl: string | undefined = link?.short_url;

    await supabaseAdmin
      .from("submissions")
      .update({ razorpay_payment_link_id: linkId })
      .eq("id", submissionId);

    if (!shortUrl) throw new Error("Razorpay did not return a payment URL.");
    return { url: shortUrl, submissionId };
  });

export const confirmCheckout = createServerFn({ method: "POST" })
  .inputValidator((input: {
    submissionId: string;
    paymentId?: string;
    paymentLinkId?: string;
    referenceId?: string;
    status?: string;
    signature?: string;
  }) => {
    if (!input?.submissionId) throw new Error("submissionId required");
    return {
      submissionId: input.submissionId,
      paymentId: input.paymentId ?? "",
      paymentLinkId: input.paymentLinkId ?? "",
      referenceId: input.referenceId ?? "",
      status: input.status ?? "",
      signature: input.signature ?? "",
    };
  })
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/admin.server");
    const { keyId, keySecret } = rzpKeys();

    let verifiedPaid = false;

    // 1) Verify the Razorpay callback signature if present
    if (data.paymentId && data.paymentLinkId && data.signature && data.status) {
      const payload = `${data.paymentLinkId}|${data.referenceId}|${data.status}|${data.paymentId}`;
      const expected = await hmacSha256Hex(keySecret, payload);
      if (expected === data.signature.toLowerCase() && data.status === "paid") {
        verifiedPaid = true;
      }
    }

    // 2) Fallback: fetch the payment link status straight from Razorpay
    if (!verifiedPaid) {
      let linkId = data.paymentLinkId;
      if (!linkId) {
        const { data: sub } = await supabaseAdmin
          .from("submissions")
          .select("razorpay_payment_link_id")
          .eq("id", data.submissionId)
          .maybeSingle();
        linkId = sub?.razorpay_payment_link_id ?? "";
      }
      if (linkId) {
        const res = await fetch(`https://api.razorpay.com/v1/payment_links/${linkId}`, {
          headers: { Authorization: basicAuth(keyId, keySecret) },
        });
        if (res.ok) {
          const link = await res.json();
          if (link?.status === "paid") verifiedPaid = true;
        }
      }
    }

    if (!verifiedPaid) return { paid: false as const };

    const { data: updated } = await supabaseAdmin
      .from("submissions")
      .update({
        status: "paid",
        paid_at: new Date().toISOString(),
        razorpay_payment_id: data.paymentId || null,
      })
      .eq("id", data.submissionId)
      .in("status", ["pending_payment", "paid"])
      .select("id, status, paid_at")
      .maybeSingle();

    // Affiliate commission: extra best-effort step AFTER the order is marked
    // paid. Idempotent (unique submission_id) and never throws.
    if (updated?.status === "paid") {
      try {
        const { recordAffiliateCommissionForSubmission } = await import("@/lib/affiliate-commission.server");
        await recordAffiliateCommissionForSubmission(supabaseAdmin, data.submissionId);
      } catch (err) {
        console.error("affiliate commission step failed (non-fatal)", err);
      }
    }

    return { paid: true as const, submission: updated };
  });
